import {
  Bell,
  ChartNoAxesCombined,
  Building2,
  FileText,
  GraduationCap,
  Home,
  Inbox,
  KeyRound,
  LogOut,
  Menu,
  Newspaper,
  Plus,
  Route,
  ReceiptText,
  ShieldCheck,
  X,
} from "lucide-react";
import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { Brand } from "../components/Brand";
import {
  isPublicModuleEnabled,
  useMunicipalModuleVisibility,
} from "../features/access/useModuleVisibility";
import { useAuth } from "../features/auth/AuthProvider";

export function AppLayout({ management = false }: { management?: boolean }) {
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const location = useLocation();
  const { data: moduleVisibility = {} } = useMunicipalModuleVisibility(
    user?.municipalityId ?? "",
  );
  const citizenNav = [
    ...(user?.roles.includes("citizen")
      ? [
        {
          to: "/mi",
          label: "Resumen",
          icon: Home,
          end: true,
          moduleCode: "citizen.dashboard",
        },
        {
          to: "/mi/tramites",
          label: "Mis trámites",
          icon: FileText,
          moduleCode: "citizen.cases",
        },
        {
          to: "/mi/tramites/nuevo",
          label: "Nueva solicitud",
          icon: Plus,
          moduleCode: "citizen.cases",
        },
        {
          to: "/mi/notificaciones",
          label: "Notificaciones",
          icon: Bell,
          moduleCode: "citizen.notifications",
        },
        {
          to: "/mi/pagos",
          label: "Mis pagos",
          icon: ReceiptText,
          moduleCode: "citizen.payments",
        },
        ]
      : []),
    ...(user?.roles.includes("student")
      ? [
        {
          to: "/academia",
          label: "Academia",
          icon: GraduationCap,
          end: true,
          moduleCode: "citizen.academy",
        },
        ]
      : []),
  ];
  const hasRole = (allowed: string[]) =>
    user?.roles.some((role) => allowed.includes(role));
  const canUseMunicipalManagement = hasRole([
    "super_admin",
    "municipal_admin",
    "mayor",
    "general_secretary",
    "system_admin",
    "intake_supervisor",
    "intake_agent",
    "fleet_operator",
    "designer",
    "news_editor",
    "treasury_agent",
    "auditor",
    "security_officer",
    "executive_viewer",
  ]);
  const managementNav = [
    ...(canUseMunicipalManagement
      ? [
          {
            to: "/gestion",
            label: hasRole([
              "super_admin",
              "municipal_admin",
              "mayor",
              "general_secretary",
              "auditor",
              "executive_viewer",
            ])
              ? "Panel ejecutivo"
              : "Resumen",
            icon: hasRole([
              "super_admin",
              "municipal_admin",
              "mayor",
              "general_secretary",
              "auditor",
              "executive_viewer",
            ])
              ? ChartNoAxesCombined
              : Home,
            end: true,
            moduleCode: "management.dashboard",
          },
        ]
      : []),
    ...(hasRole(["intake_supervisor", "intake_agent"])
      ? [
          {
            to: "/gestion/mesa-entrada",
            label: "Mesa de Entrada",
            icon: Inbox,
            moduleCode: "management.intake",
          },
        ]
      : []),
    ...(hasRole(["super_admin", "municipal_admin", "general_secretary", "system_admin"])
      ? [
          {
            to: "/gestion/dependencias",
            label: "Dependencias",
            icon: Building2,
            moduleCode: "management.organization",
          },
        ]
      : []),
    ...(hasRole([
      "super_admin",
      "municipal_admin",
      "mayor",
      "general_secretary",
      "system_admin",
      "security_officer",
      "auditor",
    ])
      ? [
          {
            to: "/gestion/organizacion",
            label: "Organización y accesos",
            icon: KeyRound,
            moduleCode: "management.organization",
          },
        ]
      : []),
    ...(hasRole([
      "super_admin",
      "municipal_admin",
      "mayor",
      "general_secretary",
      "system_admin",
      "auditor",
      "security_officer",
    ])
      ? [
          {
            to: "/gestion/seguridad",
            label: "Auditoría",
            icon: ShieldCheck,
            moduleCode: "management.security",
          },
        ]
      : []),
    ...(hasRole(["super_admin", "municipal_admin", "system_admin", "fleet_operator"])
      ? [
          {
            to: "/gestion/movilidad",
            label: "Movilidad y flota",
            icon: Route,
            moduleCode: "management.mobility",
          },
        ]
      : []),
    ...(hasRole([
      "super_admin",
      "municipal_admin",
      "mayor",
      "general_secretary",
      "treasury_agent",
      "auditor",
      "executive_viewer",
    ])
      ? [
          {
            to: "/gestion/pagos",
            label: "Pagos municipales",
            icon: ReceiptText,
            moduleCode: "management.payments",
          },
        ]
      : []),
    ...(hasRole([
      "super_admin",
      "municipal_admin",
      "mayor",
      "general_secretary",
      "system_admin",
      "designer",
      "news_editor",
    ])
      ? [
          {
            to: "/gestion/contenidos",
            label: "Contenidos e imágenes",
            icon: Newspaper,
            moduleCode: "management.content",
          },
        ]
      : []),
    ...(hasRole([
      "super_admin",
      "municipal_admin",
      "system_admin",
      "academic_admin",
      "academic_editor",
    ])
      ? [
          {
            to: "/academia/gestion",
            label: "Gestión académica",
            icon: GraduationCap,
            moduleCode: "management.academy",
          },
        ]
      : []),
  ];
  const nav = (management ? managementNav : citizenNav).filter((item) =>
    isPublicModuleEnabled(moduleVisibility, item.moduleCode),
  );

  useEffect(() => {
    setOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [open]);

  return (
    <div className="app-shell">
      <header className="app-topbar">
        <Brand compact />
        <button
          className="menu-button"
          type="button"
          aria-label={open ? "Cerrar navegación" : "Abrir navegación"}
          aria-expanded={open}
          aria-controls="app-sidebar-navigation"
          onClick={() => setOpen((value) => !value)}
        >
          {open ? <X aria-hidden="true" /> : <Menu aria-hidden="true" />}
        </button>
      </header>
      <aside
        className={`app-sidebar ${open ? "is-open" : ""}`}
        id="app-sidebar-navigation"
      >
        <div className="app-profile">
          <span>{user?.name.slice(0, 1)}</span>
          <div>
            <strong>{user?.name}</strong>
            <small>{management ? "Gestión municipal" : "Portal ciudadano"}</small>
          </div>
        </div>
        <nav aria-label={management ? "Navegación de gestión" : "Navegación ciudadana"}>
          {nav.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              {...(end === undefined ? {} : { end })}
              onClick={() => setOpen(false)}
            >
              <Icon aria-hidden="true" size={19} />
              {label}
            </NavLink>
          ))}
        </nav>
        <div className="sidebar-footer">
          <NavLink to="/">
            <Building2 aria-hidden="true" size={19} /> Volver al portal
          </NavLink>
          <button type="button" onClick={() => void logout()}>
            <LogOut aria-hidden="true" size={19} /> Cerrar sesión
          </button>
        </div>
      </aside>
      <main className="app-main" id="main-content" tabIndex={-1}>
        <Outlet />
      </main>
    </div>
  );
}

